import { Checkbox, NonIdealState } from "@blueprintjs/core";
import { IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";

import {
  BaseHealthTestRequestSearch,
  CharacteristicApi,
  CharacteristicSearch,
  CharacteristicsGridDto,
  ELab,
  EPlatform,
  HealthTestRequestApi,
} from "../../../../api";
import { CustomDataTable } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";

export interface IHealthTestListProps {
  lab: ELab;
  selectedIds: number[];
  blockedIds: number[];
  disabled: boolean;
  onSelectionUpdated: (selected: any) => void;
  healthTestRequestId: number;
  platform?: EPlatform;
}

export const HealthTestList: React.FunctionComponent<IHealthTestListProps> = ({
  lab,
  selectedIds,
  disabled,
  onSelectionUpdated,
  blockedIds,
  healthTestRequestId,
  platform = EPlatform.CARE4DOGS,
}) => {
  const { t } = useTl();
  const api = useApiService(CharacteristicApi);
  const healthTestApi = useApiService(HealthTestRequestApi);

  const { currentLanguage } = useGlobalData();
  const closableIdsList = React.useMemo(() => [], []);
  const selectionList = React.useMemo(() => selectedIds, [selectedIds]);
  const [selectAll, setselectAll] = React.useState(false);

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
    onSelectionUpdated(selectionList);
  }, [closableIdsList, onSelectionUpdated, selectAll, selectionList]);

  const columnCheckBox = React.useCallback(
    (row: CharacteristicsGridDto) => {
      if (!disabled) {
        closableIdsList.push(row.characteristicId);
      }
      return (
        <Checkbox
          checked={selectionList.includes(row.characteristicId)}
          onChange={() => {
            if (selectionList.includes(row.characteristicId)) {
              const index = selectionList.findIndex((e) => e === row.characteristicId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.characteristicId);
            }
            onSelectionUpdated(selectionList);
          }}
          disabled={disabled || (blockedIds && blockedIds.includes(row.characteristicId))}
        />
      );
    },
    [blockedIds, closableIdsList, disabled, onSelectionUpdated, selectionList]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      healthTestRequestId === null && {
        header: () => <Checkbox onChange={toggleSelectAll} disabled={disabled} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        header: () => t(ETLCodes.Name),
        fieldName: "name",
      },
      {
        header: () => t(ETLCodes.Price),
        fieldName: "price",
      },
      platform === EPlatform.CARE4DOGS && {
        header: () => t(ETLCodes.Lab),
        fieldName: "lab",
      },
    ],
    [columnCheckBox, disabled, healthTestRequestId, platform, t, toggleSelectAll]
  );

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [10, 15, 25],
    pageSize: 15,
  });

  const searchFunction = React.useCallback(
    (sObj?: CharacteristicSearch) => {
      return healthTestRequestId === null
        ? api.baseSearch(
            CharacteristicSearch.fromJS({
              ...sObj,
              languageId: currentLanguage.languageId,
              hideDysplasia: true,
              customLab: lab,
              hideProgenusDna: true,
            })
          )
        : healthTestApi.searchLinkedSingleHealthTests(
            BaseHealthTestRequestSearch.fromJS({
              ...sObj,
              languageId: currentLanguage.languageId,
              healthTestRequestId: healthTestRequestId,
              hideDnaTest: true,
            })
          );
    },
    [api, currentLanguage.languageId, healthTestApi, healthTestRequestId, lab]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  React.useEffect(() => {
    if (lab) {
      search();
    }
  }, [lab, search]);

  return lab ? (
    <CustomDataTable tableState={tableState} loading={loading} columns={columns} keyFieldName="characteristicId" />
  ) : (
    <NonIdealState icon="resolve" description={t(ETLCodes.PleaseSelectALab)} />
  );
};
